import React from 'react';

import moment from 'moment-tz';
import {getContentType} from 'sweet-helpers';

import {DocumentAlertBox} from 'app/general/structures/document-alerts/DocumentAlertBox';
import StateButton from 'app/general/widgets/StateButton';
import {markAsReviewed} from 'app/review/actions';
import {showOnReviewState} from 'app/review/hocs/showOnReviewState';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {ShowOnReviewStateInjectedProps} from 'app/review/hocs/showOnReviewState';
import type {ReviewVersionContentType} from 'sweet-types';

type Props = ShowOnReviewStateInjectedProps & {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  versionId: number;
  versionContentType: ReviewVersionContentType;
  canReview: boolean;
  onReview: () => any;
};

const BaseReviewReminderWarning = ({
  reviewDate,
  reviewDateState,
  canReview,
  versionId,
  versionContentType,
  onReview,
  contentType,
}: Props) => {
  const reviewDateMoment = moment(reviewDate);
  const reviewDateFormated = <b>{reviewDateMoment.format('ll')}</b>;
  const reviewDateToNow = <b>{reviewDateMoment.fromNow()}</b>;
  const pastDue = reviewDateState === 'overdue';
  const docType = capitalizeFirstLetter(getContentType(contentType, gt).name);

  const actions = useAsyncActions({
    markAsReviewed,
  });

  return (
    <DocumentAlertBox
      type={pastDue ? 'danger' : 'warning'}
      icon="fa-certificate"
      title={
        pastDue
          ? gt('%(docType)s needs review', {
              docType,
            })
          : gt('%(docType)s review date approaching', {
              docType,
            })
      }
      content={
        pastDue
          ? gt(
              'The review date was on %s (%s).',
              reviewDateFormated,
              reviewDateToNow
            )
          : gt(
              'The next review date is on %s (%s).',
              reviewDateFormated,
              reviewDateToNow
            )
      }
      actionContent={
        canReview ? (
          <div className="mt-2 mt-md-0">
            <StateButton
              onClick={() =>
                actions
                  .markAsReviewed(versionId, versionContentType)
                  .then(() => onReview())
              }
              busyText={gt('Marking as reviewed...')}
              className="btn alert-link"
            >
              {gt('Mark as Reviewed')}
            </StateButton>
          </div>
        ) : null
      }
    />
  );
};

export const ReviewReminderWarning = showOnReviewState<Props>([
  'overdue',
  'reminder_time',
])(BaseReviewReminderWarning);
